// client/src/coms/imagePrep.js
// Lightweight image preparation before upload: square crop or fit, resize, re-encode to PNG.
// Animated GIF/WebP are uploaded unchanged (a canvas would drop the animation); the server
// validates, resizes and re-encodes every file anyway.

export function isProbablyAnimated(file) {
  const type = String((file && file.type) || '').toLowerCase();
  return type === 'image/gif' || type === 'image/webp';
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not an image this browser can read.'));
    };
    img.src = url;
  });
}

// mode: 'fit' keeps the whole image (transparent padding), 'crop' takes the centre square.
export function computeDrawRect(srcW, srcH, size, mode) {
  if (mode === 'crop') {
    const side = Math.min(srcW, srcH);
    return { sx: (srcW - side) / 2, sy: (srcH - side) / 2, sw: side, sh: side, dx: 0, dy: 0, dw: size, dh: size };
  }
  const scale = Math.min(size / srcW, size / srcH, 1);
  const dw = Math.round(srcW * scale);
  const dh = Math.round(srcH * scale);
  return { sx: 0, sy: 0, sw: srcW, sh: srcH, dx: Math.round((size - dw) / 2), dy: Math.round((size - dh) / 2), dw, dh };
}

export async function prepareImageFile(file, { size = 320, mode = 'fit' } = {}) {
  if (!file) throw new Error('No file selected.');
  if (isProbablyAnimated(file)) return file;
  const { img, url } = await loadImageFromFile(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const r = computeDrawRect(img.naturalWidth, img.naturalHeight, size, mode);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy, r.dw, r.dh);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    if (!blob) throw new Error('Could not prepare the image.');
    return new File([blob], (file.name || 'image').replace(/\.[^.]+$/, '') + '.png', { type: 'image/png' });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function uploadMultipart(path, fields, file) {
  const fd = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v));
  if (file) fd.append('file', file);
  const res = await fetch(`/api/coms${path}`, { method: 'POST', credentials: 'include', body: fd });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const err = new Error((data && data.error) || `Upload failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Like uploadMultipart, with upload progress (fetch cannot report it). onProgress(0..1).
export function uploadWithProgress(path, fields, file, onProgress) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/coms${path}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === 'function') onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {}
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
      const err = new Error((data && data.error) || `Upload failed (${xhr.status})`);
      err.status = xhr.status;
      err.data = data;
      reject(err);
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(fd);
  });
}
